import * as React from 'react';

import { Badge, type BadgeProps } from '@/hen-ui/atomics/Badge';
import { Button } from '@/hen-ui/atomics/Button';
import { Icon } from '@/hen-ui/atomics/Icon';
import { Heading } from '@/hen-ui/atomics/Heading';
import { Image } from '@/hen-ui/atomics/Image';
import { LineClamp } from '@/hen-ui/atomics/LineClamp';
import { Link } from '@/hen-ui/atomics/Link';
import { Separator } from '@/hen-ui/atomics/Separator';
import { Text } from '@/hen-ui/atomics/Text';
import { CardRoot } from '@/hen-ui/composites/Card/Card';
import { CheckboxButton } from '@/hen-ui/composites/CheckboxButton';
import { Flex } from '@/hen-ui/composites/Flex';
import { useExpandableClamp } from '@/hen-ui/hooks';

import {
  editorialListingBrandingColumnVariants,
  editorialListingContentColumnVariants,
  editorialListingLogoImageVariants,
  editorialListingLogoShellVariants,
  editorialListingMobileLogoRowVariants,
  editorialListingMobileVisitVariants,
  editorialListingReadMoreLinkVariants,
  editorialListingReadMoreToggleVariants,
  editorialListingRootVariants,
  editorialListingSeparatorLineVariants,
  editorialListingSeparatorTrackVariants,
  editorialListingTagBadgeVariants,
  editorialListingVisitButtonVariants,
} from './editorial-listing.variants';

/** Shared fields for EditorialListing (read-more mode is a discriminated union below). */
interface EditorialListingSharedProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'children' | 'className'> {
  /** Institution / college name */
  collegeName: string;
  /** Degree or program title */
  title: string;
  /** Skill/tag labels rendered as badges */
  tags?: string[];
  /**
   * Badge variant for the tag pills. Defaults to `secondary`, which draws its
   * text from the tenant's `secondary-700` token — pick another variant when a
   * tenant's secondary brand color reads wrong on the tag row (e.g. `light`
   * swaps to `primary-700`). `null` and `undefined` both resolve to the
   * default, so clearing the field in an authoring UI restores `secondary`
   * rather than stripping the variant.
   */
  tagVariant?: BadgeProps['variant'];
  /** Description blurb (clamped; expand or link out via readMoreBehavior) */
  description?: string;
  /** Label for read more control (expand toggle or link) */
  readMoreLabel?: string;
  /**
   * When `true` (expand mode only), the description stays fully hidden until
   * the read-more toggle (e.g. "View School Details") is clicked, instead of
   * showing a clamped 3-line preview. The toggle is always rendered when a
   * description is present so the user can reveal it. Defaults to `false` to
   * preserve the existing clamped-preview behavior.
   */
  hideDescriptionUntilExpanded?: boolean;
  /**
   * When `true` (expand mode only), renders a chevron next to the read-more
   * toggle — `chevron-down` while collapsed, `chevron-up` while expanded —
   * matching the editorial-listing "View School Details" treatment. Defaults to
   * `false` so existing usages keep a text-only toggle.
   */
  showReadMoreIcon?: boolean;
  /** School logo image URL */
  logoSrc: string;
  /** Alt text for logo (defaults to collegeName) */
  logoAlt?: string;
  /**
   * URL for the "Visit Site" CTA. Optional: omit it to dispatch a
   * `navigation.linkout` through `onVisitSiteClick` instead of a static href.
   */
  visitSiteHref?: string;
  /** Label for Visit Site CTA */
  visitSiteLabel?: string;
  /**
   * Click handler for the "Visit Site" CTA. Without `visitSiteHref` the CTA
   * renders as a `<button>` and this is the sole navigation — the seam a DSL
   * `_action.onVisitSiteClick` wires (e.g. `navigation.linkout`). With
   * `visitSiteHref` it fires alongside native navigation. Mirrors
   * `DegreeCardDetailed`'s `onCtaClick`.
   */
  onVisitSiteClick?: React.MouseEventHandler<
    HTMLButtonElement | HTMLAnchorElement
  >;
  /** Show a Compare checkbox alongside the Visit Site CTA. Default: false */
  compare?: boolean;
  /** Controlled checked state for the Compare checkbox. */
  compareChecked?: boolean;
  /** When true, the Compare control renders but is not interactive (e.g. max selections reached). */
  compareDisabled?: boolean;
  /** Called when the Compare checkbox checked state changes. */
  onCompareChange?: (checked: boolean) => void;
  /** Label for the Compare checkbox. Default: "Compare" */
  compareLabel?: string;
}

/**
 * `expand` — button toggles full description in place (DegreeCardDetailed pattern).
 * `readMoreHref` is unused in this mode.
 */
export type EditorialListingExpandProps = EditorialListingSharedProps & {
  readMoreBehavior?: 'expand';
  readMoreHref?: string;
};

/**
 * `link` — clamped text + "Read more" navigates to `readMoreHref` (required).
 */
export type EditorialListingLinkProps = EditorialListingSharedProps & {
  readMoreBehavior: 'link';
  readMoreHref: string;
};

export type EditorialListingProps =
  | EditorialListingExpandProps
  | EditorialListingLinkProps;

const DEFAULT_READ_MORE = 'Read More';
const DEFAULT_READ_LESS = 'Read less';
const DEFAULT_VISIT = 'Visit Site';
const DEFAULT_COMPARE = 'Compare';
const DEFAULT_TAG_VARIANT: BadgeProps['variant'] = 'secondary';

/**
 * Editorial listing card for degree/program editorial surfaces.
 * Desktop: content | vertical separator | branding (logo + Visit Site).
 * Mobile: logo, content, full-width Visit Site.
 */
export const EditorialListing = React.forwardRef<
  HTMLDivElement,
  EditorialListingProps
>(
  (
    {
      collegeName,
      title,
      tags,
      tagVariant,
      description = '',
      readMoreBehavior = 'expand',
      readMoreHref,
      readMoreLabel = DEFAULT_READ_MORE,
      hideDescriptionUntilExpanded = false,
      showReadMoreIcon = false,
      logoSrc,
      logoAlt,
      visitSiteHref,
      visitSiteLabel = DEFAULT_VISIT,
      onVisitSiteClick,
      compare = false,
      compareChecked,
      compareDisabled = false,
      onCompareChange,
      compareLabel = DEFAULT_COMPARE,
      ...props
    },
    ref,
  ) => {
    const resolvedLogoAlt = logoAlt ?? collegeName;
    const useExpand = readMoreBehavior === 'expand';
    // Hide-until-expand only applies to the expand toggle behavior; in `link`
    // mode there is no in-place reveal to gate.
    const hideUntilExpanded = useExpand && hideDescriptionUntilExpanded;
    const descriptionId = React.useId();
    const {
      ref: descriptionRef,
      expanded: descriptionExpanded,
      isTruncated: isDescriptionTruncated,
      toggleExpanded,
    } = useExpandableClamp(description);

    // When hiding until expanded, the clamped preview is suppressed while
    // collapsed; the text only renders once expanded.
    const showDescriptionText = !(hideUntilExpanded && !descriptionExpanded);
    // Force the toggle to appear in hide-until-expand mode even though there is
    // no measured truncation (the preview is hidden, so `isDescriptionTruncated`
    // never trips) — otherwise the user would have no way to reveal the text.
    const showExpandToggle =
      useExpand &&
      (descriptionExpanded || isDescriptionTruncated || hideUntilExpanded);

    const descriptionBlock =
      description.length > 0 ? (
        <>
          {showDescriptionText && (
            <LineClamp
              ref={descriptionRef}
              id={descriptionId}
              lines={useExpand ? (descriptionExpanded ? 'none' : 3) : 3}
            >
              <Text variant='b4' color='default' tag='p'>
                {description}
              </Text>
            </LineClamp>
          )}
          {showExpandToggle && (
            <Button
              type='button'
              variant='textOnly'
              size='sm'
              onClick={(event) => {
                // Keep expand/collapse from bubbling to a product-root onClick
                // if clickPropName wiring is missing (EDUN-14734).
                event.preventDefault();
                event.stopPropagation();
                toggleExpanded();
              }}
              className={editorialListingReadMoreToggleVariants()}
              aria-expanded={descriptionExpanded}
              aria-controls={descriptionId}
            >
              {descriptionExpanded ? DEFAULT_READ_LESS : readMoreLabel}
              {showReadMoreIcon && (
                <Icon
                  icon={descriptionExpanded ? 'chevron-up' : 'chevron-down'}
                  aria-hidden={true}
                />
              )}
            </Button>
          )}
          {!useExpand && readMoreHref ? (
            <Link
              variant='primary'
              underline='always'
              href={readMoreHref}
              size='sm'
              className={editorialListingReadMoreLinkVariants()}
            >
              {readMoreLabel}
            </Link>
          ) : null}
        </>
      ) : null;

    const contentColumn = (
      <Flex
        direction='column'
        gap='sm'
        className={editorialListingContentColumnVariants()}
      >
        <Flex direction='column' gap='xxs'>
          <Text variant='b5' weight='bold' tag='span' color='heading'>
            {collegeName}
          </Text>
          <Heading variant='h4' color='default'>
            {title}
          </Heading>
        </Flex>
        {tags && tags.length > 0 ? (
          <Flex direction='row' gap='xxs' wrap='wrap' role='list'>
            {tags.map((tag, index) => (
              <span key={`${tag}-${index}`} role='listitem'>
                <Badge
                  // CVA reads an explicit `null` as opting out of the variant
                  // entirely rather than falling back to its default, so a
                  // cleared authoring field must be coerced, not passed through.
                  variant={tagVariant ?? DEFAULT_TAG_VARIANT}
                  size='sm'
                  className={editorialListingTagBadgeVariants()}
                >
                  {tag}
                </Badge>
              </span>
            ))}
          </Flex>
        ) : null}
        {descriptionBlock}
      </Flex>
    );

    const logoBlock = (layout: 'mobile' | 'desktop') => (
      <div
        className={editorialListingLogoShellVariants({
          layout: layout === 'mobile' ? 'mobile' : 'desktop',
        })}
      >
        <Image
          src={logoSrc}
          alt={resolvedLogoAlt}
          width={92}
          height={46}
          rounded='xs'
          objectFit='contain'
          loading='lazy'
          decoding='async'
          className={editorialListingLogoImageVariants()}
        />
      </div>
    );

    const visitButton = (fullWidth: boolean) => {
      const widthClassName = editorialListingVisitButtonVariants({
        width: fullWidth ? 'full' : 'auto',
      });
      // Treat a whitespace-only href as absent (mirrors DegreeCard's
      // `ctaHref?.trim()`) so the CTA renders as a button and the dispatched
      // handler (e.g. a DSL navigation.linkout) is the sole navigation.
      const resolvedVisitSiteHref = visitSiteHref?.trim();
      if (!resolvedVisitSiteHref) {
        if (!onVisitSiteClick) return null;
        return (
          <Button
            type='button'
            variant='primaryFill'
            size='lg'
            onClick={onVisitSiteClick}
            className={widthClassName}
          >
            {visitSiteLabel}
            <Icon icon='external-link' aria-hidden={true} />
          </Button>
        );
      }
      return (
        <Button
          variant='primaryFill'
          size='lg'
          href={resolvedVisitSiteHref}
          target='_blank'
          rel='noopener noreferrer'
          onClick={onVisitSiteClick}
          className={widthClassName}
        >
          {visitSiteLabel}
          <Icon icon='external-link' aria-hidden={true} />
        </Button>
      );
    };

    // The `data-editorial-listing-footer` marker lets a wrapping click
    // handler (e.g. the DSL card-click guard) distinguish the Compare
    // control from the card body so toggling Compare never fires a
    // product/navigation click.
    const compareControl = compare ? (
      <div data-editorial-listing-footer className='w-full'>
        <CheckboxButton
          size='lg'
          label={compareLabel}
          checked={compareChecked}
          disabled={compareDisabled}
          className='w-full justify-center'
          onCheckedChange={
            onCompareChange
              ? (checked: boolean | 'indeterminate') =>
                  onCompareChange(checked === true)
              : undefined
          }
        />
      </div>
    ) : null;

    return (
      <CardRoot
        ref={ref}
        variant='background-white'
        border='stroke'
        role='article'
        aria-label={title}
        className={editorialListingRootVariants()}
        {...props}
      >
        <Flex
          direction='column'
          align='start'
          className={editorialListingMobileLogoRowVariants()}
        >
          {logoBlock('mobile')}
        </Flex>

        {contentColumn}

        <div className={editorialListingSeparatorTrackVariants()}>
          <Separator
            orientation='vertical'
            decorative
            className={editorialListingSeparatorLineVariants()}
          />
        </div>

        <div className={editorialListingBrandingColumnVariants()}>
          {logoBlock('desktop')}
          {visitButton(compare)}
          {compareControl}
        </div>

        <div className={editorialListingMobileVisitVariants()}>
          {visitButton(true)}
          {compareControl}
        </div>
      </CardRoot>
    );
  },
);

EditorialListing.displayName = 'EditorialListing';
